import posthog from "posthog-js";
import { identifyUser as _identifyUser, IS_PROD } from "./usePostHog";
import { getAgeBracket } from "@/utilities";

const ANALYTICS_CONTEXT_KEY = "analytics_experiment_context";

// Session-wide identity context attached to every event.
let globalContext = {};

// Experiment context persisted to localStorage so it survives the signing
// provider redirect back to /thank-you.
let experimentContext = JSON.parse(localStorage.getItem(ANALYTICS_CONTEXT_KEY) || "{}");

/**
 * Sync the contact properties (age_bracket, state) to PostHog
 * immediately after a profile or purchase-flow save. Call this whenever
 * date_of_birth or state may have changed on the contact record.
 */
export function syncContactPersonProperties(contact) {
  if (!IS_PROD) return;
  try {
    posthog.setPersonProperties({
      age_bracket: getAgeBracket(contact?.date_of_birth),
      state: contact?.state ?? null,
    });
  } catch {}
}

export function buildInvestorProfile(contact, investments = [], segment = "sign_in") {
  const hasSignedInvestments = investments.some((inv) => inv.signature_date);
  return {
    segment,
    ageBracket: getAgeBracket(contact?.date_of_birth),
    state: contact?.state ?? null,
    investorType: hasSignedInvestments ? "repeat" : "first_time",
    signupAt: contact?.created_date ?? null,
  };
}

export function resetAnalyticsContext() {
  globalContext = {};
  experimentContext = {};
  localStorage.removeItem(ANALYTICS_CONTEXT_KEY);
}

export function experimentAssigned({
  experimentKey,
  variant,
  productParameter = null,
  entryUrl = null,
} = {}) {
  experimentContext = {
    ...experimentContext,
    experiment_key: experimentKey,
    variant,
    product_parameter: productParameter,
  };
  localStorage.setItem(ANALYTICS_CONTEXT_KEY, JSON.stringify(experimentContext));
  try {
    if (IS_PROD) {
      posthog.setPersonProperties({
        experiment_variant: variant,
        experiment_product_parameter: productParameter,
        experiment_assigned_at: new Date().toISOString(),
      });
    }
    posthog.capture("experiment_assigned", {
      ...globalContext,
      ...experimentContext,
      experiment_key: experimentKey,
      entry_url: entryUrl,
    });
  } catch {}
}

export function useAnalytics() {
  function track(eventName, properties = {}) {
    if (!IS_PROD) return;
    try {
      posthog.capture(eventName, {
        ...globalContext,
        ...experimentContext,
        ...properties,
      });
    } catch {
      // PostHog may not be initialized yet. Never throw from a track call.
    }
  }

  function trackScreen(section, screen, extra = {}) {
    track("screen_viewed", { section, screen, ...extra });
  }

  // ---- identity + context -------------------------------------------------

  /**
   * Identify the logged-in contact. Sets the PostHog identity (idempotent via
   * usePostHog) and seeds globalContext so every subsequent event carries
   * investor_id and is_authenticated.
   */
  function identifyContact(contact) {
    _identifyUser(contact);
    setGlobalContext({ investor_id: contact?.record_id, is_authenticated: true });
  }

  function setPersonProperties(props = {}) {
    if (!IS_PROD) return;
    try {
      posthog.setPersonProperties(props);
    } catch {}
  }

  function setGlobalContext(partial) {
    globalContext = { ...globalContext, ...partial };
  }

  /**
   * Set the segmentation every weekly metric splits on. Call after login /
   * profile load. Stored on the PostHog person so funnels can be broken down
   * by new-vs-existing investor, age, state, and investor type.
   */
  function setInvestorProfile({ segment, ageBracket, state, investorType, signupAt } = {}) {
    setPersonProperties({
      investor_segment: segment,
      age_bracket: ageBracket,
      state,
      investor_type: investorType,
      signup_at: signupAt,
    });
    if (segment) setGlobalContext({ investor_segment: segment });
  }

  function setExperimentContext(partial) {
    experimentContext = { ...experimentContext, ...partial };
    localStorage.setItem(ANALYTICS_CONTEXT_KEY, JSON.stringify(experimentContext));
  }

  /** Back-compat alias used by the purchase flow. */
  function updateExperimentContext(partial) {
    setExperimentContext(partial);
  }

  // ---- auth ---------------------------------------------------------------

  function signupStarted(props = {}) {
    track("signup_started", props);
  }
  function signupSubmitted({ method = "email", ...props } = {}) {
    track("signup_submitted", { method, ...props });
  }
  function emailVerificationSent(props = {}) {
    track("email_verification_sent", props);
  }
  function emailVerified(props = {}) {
    track("email_verified", props);
  }
  function loginSucceeded({ method = "email", ...props } = {}) {
    track("login_succeeded", { method, ...props });
  }
  function loginFailed(props = {}) {
    track("login_failed", props);
  }
  function passwordResetRequested(props = {}) {
    track("password_reset_requested", props);
  }

  // ---- dashboard ----------------------------------------------------------

  function dashboardViewed(props = {}) {
    track("dashboard_viewed", props);
  }
  function dashboardCtaClicked(ctaLabel, props = {}) {
    track("dashboard_cta_clicked", { cta_label: ctaLabel, ...props });
  }
  function makeInvestmentClicked(source, props = {}) {
    track("make_investment_clicked", { source, ...props });
  }

  // ---- global chrome (persistent header + widgets) -----------------------

  function callButtonClicked(sourceScreen, props = {}) {
    track("call_button_clicked", { source_screen: sourceScreen, ...props });
  }
  function themeToggled(theme, props = {}) {
    track("theme_toggled", { theme, ...props }); // theme: "light" | "dark"
  }
  function logoutClicked(props = {}) {
    track("logout_clicked", props);
  }
  function navItemClicked(item, props = {}) {
    track("nav_item_clicked", { item, ...props });
  }

  // ---- investments list --------------------------------------------------

  function investmentsTabViewed(tab, props = {}) {
    track("investments_tab_viewed", { tab, ...props });
  }
  function investmentsFilterApplied(filterType, filterValue, props = {}) {
    track("investments_filter_applied", {
      filter_type: filterType,
      filter_value: filterValue,
      ...props,
    });
  }
  function investmentDetailsOpened(investment, props = {}) {
    // investment: { investment_id, type, status, amount }
    track("investment_details_opened", { ...investment, ...props });
  }
  function statementDownloaded(props = {}) {
    track("statement_downloaded", props);
  }

  // ---- investment details ------------------------------------------------
  // Tabs: todo_items, investment_info, earnings, banking_info,
  // reinvestments (labeled "rollover" when available), conversions,
  // exchange_notes, title_transfers, signed_documents.

  function investmentTabViewed(tab, investment = {}, props = {}) {
    track("screen_viewed", {
      section: "investment_details",
      screen: "investment_details",
      tab,
      ...investment, // investment_id, investment_type, type_tag, status
      ...props,
    });
  }
  function reinvestClicked(investmentId, amount, props = {}) {
    track("reinvest_clicked", { investment_id: investmentId, amount, ...props });
  }
  /**
   * Details CTA on a row inside Reinvestments / Conversions / Title Transfers.
   * relationship: "reinvestment" | "conversion" | "title_transfer"
   */
  function relatedInvestmentOpened(relationship, parentInvestmentId, investment = {}, props = {}) {
    track("investment_details_opened", {
      ...investment, // investment_id, investment_type, status, amount, type_tag
      source: relationship,
      relationship,
      parent_investment_id: parentInvestmentId,
      ...props,
    });
  }
  function todoItemClicked(itemType, props = {}) {
    track("todo_item_clicked", { item_type: itemType, ...props });
  }
  function documentPreviewed(documentType, props = {}) {
    track("document_previewed", { document_type: documentType, ...props });
  }
  function documentDownloaded(documentType, props = {}) {
    track("document_downloaded", { document_type: documentType, ...props });
  }
  function bankingInfoEditClicked(accountKind, props = {}) {
    track("banking_info_edit_clicked", { account_kind: accountKind, ...props });
  }

  // ---- profile -----------------------------------------------------------

  function profileSectionViewed(tab, props = {}) {
    track("screen_viewed", { section: "profile", screen: "profile", tab, ...props });
  }
  function profileEditOpened(section, props = {}) {
    track("profile_edit_opened", { section, ...props });
  }
  function profileEditSaved(section, fieldsChanged = [], props = {}) {
    track("profile_edit_saved", { section, fields_changed: fieldsChanged, ...props });
  }
  function jointContactAdded(relationship, props = {}) {
    track("joint_contact_added", { relationship, ...props });
  }
  function entityAdded(entityType, props = {}) {
    track("entity_added", { entity_type: entityType, ...props });
  }
  function connectBankAccountClicked(props = {}) {
    track("connect_bank_account_clicked", props);
  }
  function bankAccountAdded(accountType, props = {}) {
    track("bank_account_added", { account_type: accountType, ...props });
  }
  function questionnaireSubmitted(props = {}) {
    track("questionnaire_submitted", props);
  }
  function bankAccountAppliedToInvestments(accountId, investmentCount, props = {}) {
    track("bank_account_applied_to_investments", {
      bank_account_id: accountId,
      investment_count: investmentCount,
      ...props,
    });
  }

  // ---- documents + contact -----------------------------------------------

  function contactFormSubmitted(topic, props = {}) {
    track("contact_form_submitted", { topic, ...props });
  }

  // ---- form fields (validation-aware, NEVER captures values) -------------

  /**
   * Fire every time a field fails validation. message is the UI string
   * (e.g. "Please enter a value for SSN") — never the typed value.
   */
  function fieldValidationFailed(
    field,
    { form = null, step = null, substep = null, errorType = null, message = null } = {},
    props = {}
  ) {
    track("field_validation_failed", {
      field,
      form,
      step,
      substep,
      error_type: errorType,
      message,
      ...props,
    });
  }

  // ---- purchase / invest flow --------------------------------------------

  // experimentAssigned is the module-level standalone export — re-exposed here
  // so call sites that destructure from useAnalytics() continue to work.

  function offeringPageViewed(props = {}) {
    track("offering_page_viewed", props);
  }
  function offeringCtaClicked(ctaLabel, ctaAmount, offeringType = null, props = {}) {
    if (offeringType) setExperimentContext({ offering_type: offeringType });
    track("offering_cta_clicked", {
      cta_label: ctaLabel,
      cta_amount: ctaAmount,
      offering_type: offeringType,
      ...props,
    });
  }
  /**
   * One event per flow screen. step_name must match the canonical step keys:
   *   select-offering → amount → payment-frequency → term-and-yield →
   *   investment-type → personal-information → suitability-questionnaire →
   *   [joint-information | entity-information | ira-information | accreditation] →
   *   funding-and-distribution → review-and-sign → thank-you
   */
  function flowStepEntered(stepName, extraProps = {}) {
    track("flow_step_entered", { step_name: stepName, ...extraProps });
  }
  function flowCompleted(extraProps = {}) {
    track("flow_completed", extraProps);
    experimentContext = {};
    localStorage.removeItem(ANALYTICS_CONTEXT_KEY);
  }

  /** Fired when the payment method is selected in the purchase flow. */
  function paymentMethodSelected(paymentMethod, props = {}) {
    track("payment_method_selected", { payment_method: paymentMethod, ...props });
  }
  /** Fired when the investment type is selected in the purchase flow. */
  function investmentTypeSelected(investmentType, props = {}) {
    track("investment_type_selected", { investment_type: investmentType, ...props });
  }

  // ---- reinvest ----------------------------------------------------------

  function reinvestStarted(investmentId, amount, props = {}) {
    track("reinvest_started", { investment_id: investmentId, amount, ...props });
  }
  function reinvestCompleted(investmentId, amount, props = {}) {
    track("reinvest_completed", { investment_id: investmentId, amount, ...props });
  }

  // ---- back-compat aliases (v1 names still used by existing call sites) --
  const trackExperimentAssigned = experimentAssigned;
  const trackOfferingPageViewed = offeringPageViewed;
  const trackOfferingCtaClicked = offeringCtaClicked;
  const trackFlowStepEntered = flowStepEntered;
  const trackFlowCompleted = flowCompleted;

  return {
    track,
    trackScreen,
    identifyContact,
    setPersonProperties,
    setInvestorProfile,
    setGlobalContext,
    setExperimentContext,
    updateExperimentContext,
    // auth
    signupStarted,
    signupSubmitted,
    emailVerificationSent,
    emailVerified,
    loginSucceeded,
    loginFailed,
    passwordResetRequested,
    // dashboard
    dashboardViewed,
    dashboardCtaClicked,
    makeInvestmentClicked,
    // global chrome
    callButtonClicked,
    themeToggled,
    logoutClicked,
    navItemClicked,
    // investments list
    investmentsTabViewed,
    investmentsFilterApplied,
    investmentDetailsOpened,
    statementDownloaded,
    // investment details
    investmentTabViewed,
    reinvestClicked,
    relatedInvestmentOpened,
    todoItemClicked,
    documentPreviewed,
    documentDownloaded,
    bankingInfoEditClicked,
    // profile
    profileSectionViewed,
    profileEditOpened,
    profileEditSaved,
    jointContactAdded,
    entityAdded,
    connectBankAccountClicked,
    bankAccountAdded,
    questionnaireSubmitted,
    bankAccountAppliedToInvestments,
    // documents + contact
    contactFormSubmitted,
    // form fields
    fieldValidationFailed,
    // purchase flow
    experimentAssigned,
    offeringPageViewed,
    offeringCtaClicked,
    flowStepEntered,
    flowCompleted,
    paymentMethodSelected,
    investmentTypeSelected,
    // reinvest
    reinvestStarted,
    reinvestCompleted,
    // back-compat aliases
    trackExperimentAssigned,
    trackOfferingPageViewed,
    trackOfferingCtaClicked,
    trackFlowStepEntered,
    trackFlowCompleted,
  };
}
